import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const TYPE_LABELS = { brand: 'Parent Brand', sub_brand: 'Sub Brand', category: 'Category', menu: 'Menu Group', sub_menu: 'Sub Menu', company: 'Company' };
const TYPE_PLACEHOLDERS = { brand: 'Nestle', sub_brand: 'Maggi', category: 'Grocery', menu: 'Staples', sub_menu: 'Instant Noodles', company: 'ABC Corp' };

export function MasterDataModal({ open, onOpenChange, masterModalType, editingMaster, masterForm, setMasterForm, savingMaster, onSave, masterBrands, masterCategories, masterMenu }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">
            {editingMaster ? 'Edit' : 'Add New'} {TYPE_LABELS[masterModalType] || masterModalType}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Parent Brand Selector for Sub Brand */}
          {masterModalType === 'sub_brand' && (
            <div>
              <Label>Parent Brand *</Label>
              <Select 
                value={masterForm.brand_id} 
                onValueChange={(v) => setMasterForm({ ...masterForm, brand_id: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select parent brand..." />
                </SelectTrigger>
                <SelectContent>
                  {(masterBrands || []).filter(b => b.is_active).map(brand => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(masterBrands || []).filter(b => b.is_active).length === 0 && (
                <p className="text-xs text-orange-600 mt-1">No active parent brands. Please add parent brands first.</p>
              )}
            </div>
          )}
          {/* Category + Menu Group selectors for Sub Menu */}
          {masterModalType === 'sub_menu' && (
            <>
              <div>
                <Label>Category *</Label>
                <Select 
                  value={masterForm.category_id} 
                  onValueChange={(v) => setMasterForm({ ...masterForm, category_id: v, menu_group_id: '' })}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select category..." /></SelectTrigger>
                  <SelectContent>
                    {(masterCategories || []).filter(c => c.is_active).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Menu Group *</Label>
                <Select 
                  value={masterForm.menu_group_id} 
                  onValueChange={(v) => setMasterForm({ ...masterForm, menu_group_id: v })}
                  disabled={!masterForm.category_id}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder={masterForm.category_id ? "Select menu group..." : "Select category first"} /></SelectTrigger>
                  <SelectContent>
                    {(masterMenu || []).filter(m => m.is_active).map(menu => (
                      <SelectItem key={menu.id} value={menu.id}>{menu.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label>Name *</Label>
            <Input 
              value={masterForm.name} 
              onChange={(e) => setMasterForm({ ...masterForm, name: e.target.value })} 
              placeholder={`e.g., ${TYPE_PLACEHOLDERS[masterModalType] || ''}`} 
              className="mt-1" 
            />
          </div>
          <div>
            <Label>Description (Optional)</Label>
            <Textarea 
              value={masterForm.description} 
              onChange={(e) => setMasterForm({ ...masterForm, description: e.target.value })} 
              placeholder="Brief description..." 
              className="mt-1"
              rows={2}
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch 
              checked={masterForm.is_active} 
              onCheckedChange={(v) => setMasterForm({ ...masterForm, is_active: v })} 
            />
            <Label>Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={savingMaster}>
            {savingMaster ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (editingMaster ? 'Update' : 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
